import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import AuthProvider from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./layout/Layout";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/clients/Clients";
import ClientDetail from "./pages/clients/ClientDetail";
import ClientServices from "./pages/clients/ClientServices";
import ClientJobs from "./pages/clients/ClientJobs";
import ClientInvoices from "./pages/clients/ClientInvoices";
import ClientAssignBulkServices from "./pages/clients/ClientAssignBulkServices";
import Services from "./pages/services/Services";
import Jobs from "./pages/jobs/Jobs";
import JobView from "./pages/jobs/JobView";
import AddJob from "./pages/jobs/AddJob";
import Desk from "./pages/desk/Desk";
import Tasks from "./pages/tasks/Tasks";
import Audit from "./pages/audit/Audit";
import Users from "./pages/users/Users";
import Reports from "./pages/reports/Reports";
import Settings from "./pages/settings/Settings";
import Permissions from "./pages/settings/Permissions";
import Roles from "./pages/settings/Roles";

// Permission-based guard — drives access from granular RBAC, not role names.
const Guard = ({ perm, children }) => (
  <ProtectedRoute requiredPerm={perm}>{children}</ProtectedRoute>
);

export default function App() {
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    {/* Dashboard — all authenticated users */}
                    <Route path="/" element={<Dashboard />} />

                    {/* Clients */}
                    <Route
                      path="/clients"
                      element={<Guard perm="clients.view"><Clients /></Guard>}
                    />
                    <Route
                      path="/clients/:id"
                      element={<Guard perm="clients.view"><ClientDetail /></Guard>}
                    />
                    <Route
                      path="/clients/:id/services"
                      element={<Guard perm="clients.view"><ClientServices /></Guard>}
                    />
                    <Route
                      path="/clients/:id/jobs"
                      element={<Guard perm="clients.view"><ClientJobs /></Guard>}
                    />
                    <Route
                      path="/clients/:id/invoices"
                      element={<Guard perm="clients.view"><ClientInvoices /></Guard>}
                    />
                    <Route
                      path="/client/assign-bulk-services"
                      element={<Guard perm="clients.edit"><ClientAssignBulkServices /></Guard>}
                    />

                    {/* Services master */}
                    <Route
                      path="/services"
                      element={<Guard perm="services.view"><Services /></Guard>}
                    />

                    {/* Jobs */}
                    <Route
                      path="/jobs"
                      element={<Guard perm="jobs.view"><Jobs /></Guard>}
                    />
                    <Route
                      path="/jobs/:id"
                      element={<Guard perm="jobs.view"><JobView /></Guard>}
                    />
                    <Route
                      path="/job/create"
                      element={<Guard perm="jobs.create"><AddJob /></Guard>}
                    />

                    {/* My Desk — everyone */}
                    <Route path="/my-desk" element={<Desk />} />
                    <Route path="/my-workspace" element={<Desk />} />

                    {/* Tasks — everyone */}
                    <Route path="/tasks" element={<Tasks />} />

                    {/* Audit */}
                    <Route
                      path="/audit"
                      element={<Guard perm="audit.view"><Audit /></Guard>}
                    />

                    {/* Users */}
                    <Route
                      path="/user"
                      element={<Guard perm="users.view"><Users /></Guard>}
                    />

                    {/* Reports */}
                    <Route
                      path="/reports"
                      element={<Guard perm="reports.view"><Reports /></Guard>}
                    />

                    {/* Settings */}
                    <Route
                      path="/settings"
                      element={<Guard perm="settings.manage"><Settings /></Guard>}
                    />

                    {/* Permissions */}
                    <Route
                      path="/permissions"
                      element={<Guard perm="permissions.manage"><Permissions /></Guard>}
                    />

                    {/* Roles */}
                    <Route
                      path="/roles"
                      element={<Guard perm="permissions.manage"><Roles /></Guard>}
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </>
  );
}
